/**
 * Cart Recommendations (You May Also Like)
 * - Fetches products via Shopify Section Rendering API
 * - Mobile (drawer): accordion with peek effect + horizontal carousel
 * - Desktop (drawer): slide-out panel alongside the cart drawer
 * - Cart page: full-width horizontal carousel section
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-recommendations';
  var LIMIT = 8;

  function CartRecommendations() {
    this.drawerAccordion = document.querySelector('.cart-recs--drawer');
    this.modalPanel = document.getElementById('CartRecsModal');
    this.pageSection = document.querySelector('.cart-recs--page');
    this.currentProductId = null;
    this.addedProductIds = {};

    if (!this.drawerAccordion && !this.pageSection) return;

    this._setupAccordion();
    this._setupModal();
    this._watchCart();
    this._watchCartChanges();

    // Cart page: product ID is set via Liquid data attribute
    if (this.pageSection && this.pageSection.dataset.productId) {
      // Set currentProductId so MutationObserver won't double-fire for same product
      this.currentProductId = this.pageSection.dataset.productId;
      this._load(this.currentProductId);
    }
  }

  CartRecommendations.prototype = {

    /**
     * Watch [data-products] for cart updates; re-fetch when first product changes.
     */
    _watchCart: function () {
      var self = this;
      var productsEl = document.querySelector('[data-products]');
      if (!productsEl) return;

      // Try on init (cart page already has items)
      self._tryLoadFromEl(productsEl);

      // Observe future cart mutations (quantity changes, new items)
      var observer = new MutationObserver(function () {
        self._tryLoadFromEl(productsEl);
      });
      observer.observe(productsEl, { childList: true, subtree: false });
    },

    _tryLoadFromEl: function (productsEl) {
      var itemsEl = productsEl.querySelector('[data-first-product-id]');
      if (!itemsEl) return;
      var pid = itemsEl.dataset.firstProductId;
      if (!pid || pid === this.currentProductId) return;
      this.currentProductId = pid;
      this._load(pid);
    },

    /**
     * On the cart page, refresh the line items whenever the cart changes by any
     * route – the recs add-to-cart (`cart:build`) or a theme ajax add such as
     * the quick-view modal (`ajaxProduct:added`). The page's own CartForm isn't
     * reliably wired up here, so we drive the refresh ourselves. Debounced so
     * overlapping events only trigger a single fetch.
     */
    _watchCartChanges: function () {
      if (!this.pageSection) return;
      var self = this;
      var pending;
      var refresh = function () {
        clearTimeout(pending);
        pending = setTimeout(function () { self._refreshCartPage(); }, 150);
      };
      document.addEventListener('cart:build', refresh);
      document.addEventListener('ajaxProduct:added', refresh);
    },

    /**
     * Fetch the cart-recommendations section from Shopify's recommendations API.
     */
    _load: function (productId) {
      var self = this;
      var url = '/recommendations/products?section_id=' + SECTION_ID +
        '&product_id=' + productId +
        '&limit=' + LIMIT +
        '&intent=related';

      fetch(url)
        .then(function (res) { return res.text(); })
        .then(function (html) { self._render(html); })
        .catch(function (e) { console.warn('[CartRecs] fetch failed:', e); });
    },

    /**
     * Parse response, extract .cart-recs__track, inject into all containers.
     */
    _render: function (html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var track = doc.querySelector('.cart-recs__track');

      if (!track) {
        // No recommendations available – hide containers
        this._hideAll();
        return;
      }

      // Inject into drawer accordion
      if (this.drawerAccordion) {
        var drawerTrack = this.drawerAccordion.querySelector('.cart-recs__track');
        if (drawerTrack) {
          drawerTrack.parentNode.replaceChild(track.cloneNode(true), drawerTrack);
        }
        this.drawerAccordion.classList.remove('cart-recs--hidden');
      }

      // Inject into desktop modal panel
      if (this.modalPanel) {
        var modalTrack = this.modalPanel.querySelector('.cart-recs__track');
        if (modalTrack) {
          modalTrack.parentNode.replaceChild(track.cloneNode(true), modalTrack);
        }
        // Update modal nav buttons state
        this._updateModalNav();
      }

      // Inject into cart page section
      if (this.pageSection) {
        var pageTrack = this.pageSection.querySelector('.cart-recs__track');
        if (pageTrack) {
          pageTrack.parentNode.replaceChild(track.cloneNode(true), pageTrack);
        }
        this.pageSection.classList.remove('cart-recs--hidden');
      }

      this._bindCardActions();
      // Keep already-added products out of freshly rendered recs.
      this._hideAddedProducts();

      // If the drawer is already open on desktop, show the panel immediately
      var cartDrawer = document.getElementById('CartDrawer');
      var drawerAlreadyOpen = cartDrawer && cartDrawer.classList.contains('drawer--is-open');
      var isDesktop = window.matchMedia('(min-width: 769px)').matches;
      if (drawerAlreadyOpen && isDesktop) {
        this._openModal();
      }
    },

    _hideAll: function () {
      if (this.drawerAccordion) this.drawerAccordion.classList.add('cart-recs--hidden');
      if (this.pageSection) this.pageSection.classList.add('cart-recs--hidden');
    },

    /**
     * Bind click/change handlers to all newly-rendered card controls:
     * add-to-cart buttons, quick-view buttons and variant selects.
     */
    _bindCardActions: function () {
      var self = this;

      document.querySelectorAll('.cart-recs__add-btn:not([data-recs-bound])').forEach(function (btn) {
        btn.setAttribute('data-recs-bound', '1');
        btn.addEventListener('click', function () { self._addToCart(btn); });
      });

      document.querySelectorAll('.cart-recs__quickview-btn:not([data-recs-bound])').forEach(function (btn) {
        btn.setAttribute('data-recs-bound', '1');
        btn.addEventListener('click', function () {
          self._openQuickView(btn.dataset.productHandle, btn.dataset.productId, btn.dataset.productUrl);
        });
      });

      // Keep the displayed price and the add button's variant id in sync with the picker.
      document.querySelectorAll('.cart-recs__variant-select:not([data-recs-bound])').forEach(function (select) {
        select.setAttribute('data-recs-bound', '1');
        select.addEventListener('change', function () { self._syncVariant(select); });
      });
    },

    /**
     * Reflect the chosen variant in the card: update price, sale state and the
     * variant id used by the add-to-cart button.
     */
    _syncVariant: function (select) {
      var item = select.closest('.cart-recs__item');
      if (!item) return;

      var option = select.options[select.selectedIndex];
      if (!option) return;

      var addBtn = item.querySelector('.cart-recs__add-btn');
      if (addBtn) addBtn.dataset.variantId = option.value;

      // The `money` filter can emit HTML (e.g. <span class="money">), so render
      // with innerHTML. Values come from our own money-filtered data attributes.
      var priceEl = item.querySelector('[data-price]');
      if (priceEl && option.dataset.price) priceEl.innerHTML = option.dataset.price;

      var compareEl = item.querySelector('[data-compare]');
      if (compareEl) {
        if (option.dataset.onSale === 'true' && option.dataset.compare) {
          compareEl.innerHTML = option.dataset.compare;
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    },

    _addToCart: function (btn) {
      var self = this;
      // Prefer the variant chosen in the picker, falling back to the default variant.
      var item = btn.closest('.cart-recs__item');
      var select = item && item.querySelector('.cart-recs__variant-select');
      var variantId = parseInt((select && select.value) || btn.dataset.variantId, 10);
      if (!variantId) return;

      var orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add failed');
          return res.json();
        })
        .then(function () {
          btn.textContent = '✓ Added';
          // Rebuild cart drawer (and the count bubbles) via the theme's mechanism.
          // On the cart page, _watchCartChanges() also picks this up to refresh
          // the line items (the page's own CartForm isn't reliably wired up).
          document.dispatchEvent(new CustomEvent('cart:build'));
          // Remember the product so it stays hidden if the recs re-render, then
          // remove its card from every container after a brief confirmation.
          var productId = btn.dataset.productId;
          if (productId) self.addedProductIds[productId] = true;
          setTimeout(function () {
            self._hideAddedProducts();
          }, 900);
        })
        .catch(function () {
          // Fallback: go to product page
          btn.disabled = false;
          btn.textContent = orig;
          var productUrl = btn.dataset.productUrl;
          if (productUrl) window.location.href = productUrl;
        });
    },

    /**
     * Remove cards for already-added products from every container (drawer
     * accordion, desktop modal, cart page). If a container ends up empty, hide
     * it / close the modal so we never show an empty recs panel.
     */
    _hideAddedProducts: function () {
      var ids = Object.keys(this.addedProductIds);
      if (!ids.length) return;

      ids.forEach(function (pid) {
        document.querySelectorAll('.cart-recs__add-btn[data-product-id="' + pid + '"]').forEach(function (btn) {
          var item = btn.closest('.cart-recs__item');
          if (item) item.remove();
        });
      });

      if (this.drawerAccordion && !this.drawerAccordion.querySelector('.cart-recs__item')) {
        this.drawerAccordion.classList.add('cart-recs--hidden');
      }
      if (this.pageSection && !this.pageSection.querySelector('.cart-recs__item')) {
        this.pageSection.classList.add('cart-recs--hidden');
      }
      if (this.modalPanel && !this.modalPanel.querySelector('.cart-recs__item')) {
        this._closeModal();
      } else {
        this._updateModalNav();
      }
    },

    /**
     * Refresh the cart-page line items after an add. The theme rebuilds the
     * drawer on `cart:build`, but the cart page's own CartForm isn't reliably
     * instantiated on this store, so /cart wouldn't otherwise reflect the add.
     * Mirrors the theme's CartForm.cartMarkup (items + subtotal) and re-binds
     * quantity selectors on the fresh markup so they keep working.
     */
    _refreshCartPage: function () {
      var t = window.theme;
      var pageForm = document.getElementById('CartPageForm');
      if (!pageForm || !t || !t.cart || typeof t.cart.getCartProductMarkup !== 'function') return;

      var productsEl = pageForm.querySelector('[data-products]');
      if (!productsEl) return;

      t.cart.getCartProductMarkup().then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var items = doc.querySelector('.cart__items');
        if (!items) return;

        // Swap in the fresh line items.
        productsEl.innerHTML = '';
        productsEl.appendChild(items);

        // Update the subtotal.
        var subtotalEl = pageForm.querySelector('[data-subtotal]');
        if (subtotalEl && items.dataset.cartSubtotal != null && t.Currency && t.settings) {
          subtotalEl.innerHTML = t.Currency.formatMoney(items.dataset.cartSubtotal, t.settings.moneyFormat);
        }

        // Re-bind quantity selectors on the new markup (namespace matches the
        // page CartForm's, so any listening form still receives the events).
        if (typeof t.QtySelector === 'function') {
          pageForm.querySelectorAll('.js-qty__wrapper').forEach(function (el) {
            new t.QtySelector(el, { namespace: '.cart-page', isCart: true });
          });
        }

        if (window.AOS) { AOS.refreshHard(); }
      }).catch(function (e) { console.warn('[CartRecs] cart page refresh failed:', e); });
    },

    /*==========================================================================
      Quick view – reuses the theme's built-in quick-shop modal.
      Triggered only from the desktop recs modal and the cart page (the button
      is hidden in the mobile drawer accordion via CSS).
    ============================================================================*/

    /**
     * Open the theme's quick-shop modal for a recommended product.
     * Opening any modal dispatches `modalOpen`, which the cart drawer listens
     * for and closes itself – we also fire `cart:close` for an instant close.
     * Falls back to the product page if the quick-shop API isn't available.
     */
    _openQuickView: function (handle, productId, productUrl) {
      var t = window.theme;

      if (!handle || !productId || !t || typeof t.preloadProductModal !== 'function') {
        if (productUrl) window.location.href = productUrl;
        return;
      }

      // If the shopper opened quick view while the cart drawer was open (i.e. from
      // the desktop recs modal), reopen the cart once they leave the quick-view
      // modal – whether they added to cart, hit close, clicked away or pressed Esc.
      // On the cart page no drawer is open, so nothing reopens.
      var cartDrawer = document.getElementById('CartDrawer');
      var cameFromCart = cartDrawer && cartDrawer.classList.contains('drawer--is-open');
      if (cameFromCart) {
        this._reopenCartWhenQuickViewCloses('QuickShopModal-' + productId);
      }

      // Close the cart drawer (and the desktop recs panel) straight away.
      document.dispatchEvent(new CustomEvent('cart:close'));

      this._ensureQuickViewMarkup(handle, productId);

      var trigger = document.querySelector('.js-modal-open-quick-modal-' + productId);
      var openNow = function () { if (trigger) trigger.click(); };

      // Already fetched once → just reopen.
      if (t.quickShopPreloadState && t.quickShopPreloadState[handle] === 'loaded') {
        openNow();
        return;
      }

      // Fetch the product modal, then open it once the theme reports it ready.
      t.preloadProductModal(handle, productId);

      var attempts = 0;
      var poll = setInterval(function () {
        attempts++;
        var state = t.quickShopPreloadState && t.quickShopPreloadState[handle];
        if (state === 'loaded') {
          clearInterval(poll);
          openNow();
        } else if (attempts > 80) {
          // ~8s safety timeout
          clearInterval(poll);
          if (productUrl) window.location.href = productUrl;
        } else if (state !== 'loading' && attempts > 2) {
          // preload failed (theme deletes the state on error) → fall back
          clearInterval(poll);
          if (productUrl) window.location.href = productUrl;
        }
      }, 100);
    },

    /**
     * Inject the quick-shop modal shell + a hidden open-trigger at <body> level.
     * Must NOT live inside the cart drawer: the drawer uses a CSS transform when
     * open, which would break the fixed-position modal. Mirrors snippets/quick-shop-modal.liquid.
     */
    _ensureQuickViewMarkup: function (handle, productId) {
      var modalId = 'QuickShopModal-' + productId;
      if (document.getElementById(modalId)) return;

      var modal = document.createElement('div');
      modal.id = modalId;
      modal.className = 'modal modal--square modal--quick-shop';
      modal.setAttribute('data-product-id', productId);
      modal.innerHTML =
        '<div class="modal__inner"><div class="modal__centered">' +
          '<div class="modal__centered-content">' +
            '<div id="QuickShopHolder-' + handle + '"></div>' +
          '</div>' +
          '<button type="button" class="modal__close js-modal-close text-link" aria-label="Close">' +
            '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64">' +
            '<path d="m19 17.61 27.12 27.13m0-27.12L19 44.74"/></svg>' +
          '</button>' +
        '</div></div>';
      document.body.appendChild(modal);

      // Hidden trigger so theme.Modals (created during preload) binds an opener we can click.
      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'js-modal-open-quick-modal-' + productId;
      trigger.style.display = 'none';
      document.body.appendChild(trigger);
    },

    /**
     * Reopen the cart drawer the first time the quick-view modal goes from
     * open -> closed. Watching the active class catches every dismissal path
     * (close button, click-away, Esc, or auto-close after add-to-cart).
     * One-shot: the observer disconnects after firing.
     *
     * The reopen is deferred to a macrotask: a close-button/click-away dismissal
     * runs inside that click's event dispatch, so reopening synchronously would
     * let the same click bubble to the window handler the drawer binds on open
     * and immediately re-close it. Deferring lets the click finish first.
     */
    _reopenCartWhenQuickViewCloses: function (modalId) {
      var modal = document.getElementById(modalId);
      if (!modal || typeof MutationObserver === 'undefined') return;

      var seenOpen = false;
      var observer = new MutationObserver(function () {
        if (modal.classList.contains('modal--is-active')) {
          seenOpen = true;
        } else if (seenOpen) {
          observer.disconnect();
          setTimeout(function () {
            document.dispatchEvent(new CustomEvent('cart:open'));
          }, 50);
        }
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
    },

    /*==========================================================================
      Accordion (mobile drawer)
    ============================================================================*/
    _setupAccordion: function () {
      var self = this;
      var toggle = document.querySelector('.cart-recs__toggle');
      if (!toggle) return;

      toggle.addEventListener('click', function () {
        var accordion = toggle.closest('.cart-recs--drawer');
        var isOpen = accordion.classList.toggle('cart-recs--open');
        toggle.setAttribute('aria-expanded', String(isOpen));

        // Accordion is at the bottom of the scrollable area,
        // no need to scroll — it's already in view.
      });
    },

    /*==========================================================================
      Side panel (desktop) – auto-opens/closes with the cart drawer
    ============================================================================*/
    _setupModal: function () {
      var self = this;

      /**
       * The theme's Drawers module listens for clicks on `window` and closes
       * the drawer for anything that isn't inside `.drawer`. The YMAL modal card
       * sits outside `.drawer`, so stop propagation on the card so those clicks
       * never reach the window handler. We intentionally do NOT stop propagation
       * on the transparent overlay (#CartRecsModal itself) so clicks in the empty
       * space outside the card still behave normally.
       */
      var card = document.querySelector('.cart-recs__modal-card');
      if (card) {
        card.addEventListener('click', function (e) {
          e.stopPropagation();
        });
      }

      // Watch the cart drawer element for its open/close class
      var cartDrawer = document.getElementById('CartDrawer');
      if (cartDrawer) {
        var observer = new MutationObserver(function () {
          var drawerOpen = cartDrawer.classList.contains('drawer--is-open');
          var isDesktop = window.matchMedia('(min-width: 769px)').matches;
          if (isDesktop) {
            if (drawerOpen) {
              self._openModal();
            } else {
              self._closeModal();
            }
          }
        });
        observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
      }

      // Close when the cart drawer closes
      document.addEventListener('cart:close', function () { self._closeModal(); });

      // Carousel prev / next
      var prevBtn = document.querySelector('.cart-recs__modal-prev');
      var nextBtn = document.querySelector('.cart-recs__modal-next');

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          var track = self.modalPanel && self.modalPanel.querySelector('.cart-recs__track');
          if (track) track.scrollBy({ left: -220, behavior: 'smooth' });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          var track = self.modalPanel && self.modalPanel.querySelector('.cart-recs__track');
          if (track) track.scrollBy({ left: 220, behavior: 'smooth' });
        });
      }
    },

    /**
     * Open the modal – only if recommendations have actually loaded.
     */
    _openModal: function () {
      if (!this.modalPanel) return;
      // Don't show an empty modal card
      if (!this.modalPanel.querySelector('.cart-recs__item')) return;
      this.modalPanel.classList.add('cart-recs__modal--open');
      this.modalPanel.setAttribute('aria-hidden', 'false');
    },

    _closeModal: function () {
      if (!this.modalPanel) return;
      this.modalPanel.classList.remove('cart-recs__modal--open');
      this.modalPanel.setAttribute('aria-hidden', 'true');
    },

    _updateModalNav: function () {
      if (!this.modalPanel) return;
      var track = this.modalPanel.querySelector('.cart-recs__track');
      var prev = this.modalPanel.querySelector('.cart-recs__modal-prev');
      var next = this.modalPanel.querySelector('.cart-recs__modal-next');
      if (!track || !prev || !next) return;

      var update = function () {
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };

      track.addEventListener('scroll', update, { passive: true });
      update();
    }
  };

  // Boot after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { new CartRecommendations(); });
  } else {
    new CartRecommendations();
  }
})();
